
import { useEffect, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ConversationList } from "@/components/inbox/ConversationList";
import { MessageThread } from "@/components/inbox/MessageThread";
import { MessageInput } from "@/components/inbox/MessageInput";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useRealtimeMessages } from "@/hooks/useRealtimeMessages";

const SellerInbox = () => {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(
    null
  );
  const [newMessage, setNewMessage] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const threadEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUserId = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    fetchUserId();
  }, []);

  // Use the enhanced real-time messages hook
  const { markConversationAsRead } = useRealtimeMessages(selectedConversation, currentUserId, true);

  const { data: conversations, isLoading: isLoadingConversations } = useQuery({
    queryKey: ['cook-conversations'],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          id,
          unread_count,
          updated_at,
          cook_id,
          foodie_id,
          cook:profiles!conversations_cook_id_fkey(
            id,
            first_name,
            last_name,
            image_url
          ),
          foodie:profiles!conversations_foodie_id_fkey(
            id,
            first_name,
            last_name,
            image_url
          ),
          meal:meals(
            id,
            name,
            image
          ),
          messages(
            content,
            created_at,
            sender_id
          )
        `)
        .eq('cook_id', user?.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      console.log('SellerInbox - Fetched conversations:', data.length);
      data.forEach(conv => {
        console.log(`Conversation ${conv.id} has unread count: ${conv.unread_count}`);
      });

      return data.map((conv) => ({
        ...conv,
        last_message: conv.messages.sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0] || null,
      }));
    },
    enabled: !!currentUserId,
    staleTime: 0, // Don't cache data to ensure we always get fresh data
    refetchInterval: 5000, // Force refetch every 5 seconds to ensure unread counts are up-to-date
  });

  // Find the selected conversation data
  const selectedConversationData = conversations?.find(
    (conv) => conv.id === selectedConversation
  );

  useEffect(() => {
    // If a conversation is selected and it has unread messages, mark it as read
    if (selectedConversation && selectedConversationData?.unread_count > 0) {
      markConversationAsRead();
    }
  }, [selectedConversation, selectedConversationData, markConversationAsRead]);

  const { data: messages, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['messages', selectedConversation],
    queryFn: async () => {
      if (!selectedConversation) return null;

      const { data, error } = await supabase
        .from('messages')
        .select(`
          id,
          content,
          created_at,
          sender_id,
          sender:profiles!messages_sender_id_fkey(
            id,
            first_name,
            last_name,
            image_url
          )
        `)
        .eq('conversation_id', selectedConversation)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!selectedConversation,
    staleTime: 0, // Don't cache data to ensure we always get fresh data
  });

  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!selectedConversation || !newMessage.trim() || !currentUserId || isSending) return;

    setIsSending(true);
    const messageContent = newMessage.trim();
    setNewMessage(""); // Clear input immediately for better UX

    try {
      console.log("Sending message as cook ID:", currentUserId);
      
      // Insert the new message
      const { error: messageError } = await supabase.from("messages").insert({
        conversation_id: selectedConversation,
        content: messageContent,
        sender_id: currentUserId,
      });

      if (messageError) throw messageError;

      // Update conversation timestamp only
      // The database trigger will handle incrementing the unread count
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ 
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedConversation);

      if (updateError) throw updateError;

      console.log("Updated conversation timestamp. Unread counts will be handled by the database trigger.");

      // Invalidate queries to refresh the UI
      await queryClient.invalidateQueries({ queryKey: ['messages', selectedConversation] });
      await queryClient.invalidateQueries({ queryKey: ['cook-conversations'] });
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
      await queryClient.invalidateQueries({ queryKey: ['unread-messages', true] });
      
      console.log("Message sent successfully");
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageContent); // Restore message content on error
      toast({
        title: "Failed to send message",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setIsSending(false);
    }
  };

  if (selectedConversation) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <header className="sticky top-0 z-10 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container flex h-14 items-center">
            <Button 
              variant="ghost" 
              onClick={() => setSelectedConversation(null)}
              className="mr-2"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex flex-col">
              <h2 className="text-sm font-medium">
                {selectedConversationData?.meal?.name}
              </h2>
              <p className="text-xs text-muted-foreground">
                with {selectedConversationData?.foodie?.first_name} {selectedConversationData?.foodie?.last_name}
              </p>
            </div>
          </div>
        </header>
        <div className="flex-1 overflow-hidden">
          <MessageThread 
            messages={messages || []} 
            currentUserId={currentUserId} 
            isLoading={isLoadingMessages}
            ref={threadEndRef}
          />
          <MessageInput
            value={newMessage}
            onChange={setNewMessage}
            onSend={sendMessage}
            isDisabled={isSending}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="px-[16px] py-[16px]">
        <ConversationList
          conversations={conversations || []}
          onSelectConversation={setSelectedConversation}
          currentUserId={currentUserId || ""}
          isLoading={isLoadingConversations}
        />
      </main>
    </div>
  );
};

export default SellerInbox;
